import React, { useMemo, useState } from 'react';
import { TaskWithState, NextTask, TaskState } from '../../types';
import { getHandAngles, minutesToAngle, polarToCartesian, calculateLayeredRadii } from '../../utils/clockMath';
import { calculateTimeWindow } from '../../utils/timeWindowV2';
import { TaskWedge } from '../TaskWedge/TaskWedge';
import { CLOCK_THEMES } from '../../styles/clockThemes';
import styles from './AnalogClock.module.scss';

// Logo paths for clock branding
const CLOCK_LOGO_PATHS = {
  black: '/logo/My_Minu_Logo_Version1_black.png',
  white: '/logo/My_Minu_Logo_Version1_white.png',
};

// Themes that should use white logo (dark backgrounds)
const DARK_THEMES = ['kids_dark', 'modern'];

interface AnalogClockProps {
  currentTime: Date;
  tasks: TaskWithState[];
  nextTask: NextTask;
  onChipClick: () => void;
  themeId?: string;
  onTaskClick?: (task: TaskWithState) => void;
  showPastTasks?: boolean;
  showMinuteTicks?: boolean;
  taskSortingMode?: 'default' | 'layered';
}

const CX = 250;
const CY = 250;
const CLOCK_RADIUS = 200;
const WEDGE_RADIUS = 185; // Wedges go almost to the edge

// Logo position: 45 degrees down-right, midway between center and edge
const LOGO_DISTANCE = 80; // Distance from center
const LOGO_ANGLE_RAD = (45 * Math.PI) / 180; // 45 degrees in radians
const LOGO_X = CX + LOGO_DISTANCE * Math.cos(LOGO_ANGLE_RAD);
const LOGO_Y = CY + LOGO_DISTANCE * Math.sin(LOGO_ANGLE_RAD);
const LOGO_SIZE = 50; // Logo width in SVG units
const LOGO_OPACITY = 0.15; // Subtle branding

export function AnalogClock({
  currentTime,
  tasks,
  nextTask,
  onChipClick,
  themeId = 'kids_light',
  onTaskClick,
  showPastTasks = true,
  showMinuteTicks = false,
  taskSortingMode = 'default'
}: AnalogClockProps) {
  const theme = CLOCK_THEMES[themeId] || CLOCK_THEMES['kids_light'];
  const { hour, minute, second } = getHandAngles(currentTime);

  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  // Calculate time window V2: 20 min back + 40 min forward with gradients
  const windowSegments = useMemo(() => {
    let segments = calculateTimeWindow(tasks, currentTime);

    // If hide past tasks is enabled, adjust active task segments to shrink
    // and remove completely past segments
    if (!showPastTasks) {
      const currentMs = currentTime.getTime();
      const currentMinuteInHour = currentTime.getMinutes() + currentTime.getSeconds() / 60;

      segments = segments.map(segment => {
        // 1. Strictly filter segments that are entirely in the past
        // Add a small epsilon (100ms) to avoid flickering at exact transition
        if (segment.endMs <= currentMs - 100) {
          return null;
        }

        // 2. For ACTIVE tasks, crop the start to 'now' if it's in the past
        if (segment.task.state === TaskState.ACTIVE) {
          // If segment started before now, crop it
          if (segment.startMs < currentMs) {
            return {
              ...segment,
              visibleStartMin: currentMinuteInHour,
              startMs: currentMs
            };
          }
        }
        
        return segment;
      }).filter((s): s is typeof segments[0] => s !== null);
    }

    return segments;
  }, [tasks, currentTime, showPastTasks]);

  // Calculate radii for overlapping tasks
  const taskRadii = useMemo(() => {
    const uniqueTasksMap = new Map<string, TaskWithState>();
    windowSegments.forEach(s => uniqueTasksMap.set(s.task.id, s.task));
    const uniqueTasks = Array.from(uniqueTasksMap.values());
    
    // Custom sort for layering:
    // 1. ACTIVE tasks first (so they get Layer 0 = Outer Radius)
    // 2. UPCOMING tasks next
    // 3. PAST tasks last (so they get inner layers if overlapping)
    const sortComparator = (a: TaskWithState, b: TaskWithState) => {
      const getPriority = (t: TaskWithState) => {
        if (t.state === TaskState.ACTIVE) return 3;
        if (t.state === TaskState.UPCOMING) return 2;
        return 1; // Past, Fading, Gone
      };

      const scoreA = getPriority(a);
      const scoreB = getPriority(b);

      if (scoreA !== scoreB) {
        return scoreB - scoreA; // Higher priority first
      }

      // For UPCOMING tasks, sort ASCENDING (Sooner start -> Outer layer)
      if (scoreA === 2) {
        return a.startTime.getTime() - b.startTime.getTime();
      }

      // For PAST/ACTIVE, sort DESCENDING (Later start/Most Recent -> Outer layer)
      return b.startTime.getTime() - a.startTime.getTime();
    };

    // If sorting mode is 'default', we want everything on the outermost layer (Layer 0).
    // The baseLayerExclusivePredicate controls who is allowed on Layer 0.
    // If taskSortingMode is 'default', we should allow EVERYONE on the base layer (return true).
    // This effectively disables the "push to inner layer" logic for non-active tasks.
    const baseLayerExclusivePredicate = (task: TaskWithState) => {
      if (taskSortingMode === 'default') return true;
      return task.state === TaskState.ACTIVE;
    };

    // Use negative spacing to layer inwards (185, 160, 135...)
    // If taskSortingMode is 'default', spacing 0 keeps them all at the same radius if they do end up on different layers,
    // but with the predicate above returning true, they should all be on Layer 0 anyway.
    const spacing = taskSortingMode === 'layered' ? -25 : 0;
    
    return calculateLayeredRadii(
      uniqueTasks, 
      WEDGE_RADIUS, 
      spacing, 
      sortComparator,
      baseLayerExclusivePredicate
    );
  }, [windowSegments, taskSortingMode]);

  // Calculate icon positions for unique tasks
  const taskIcons = useMemo(() => {
    if (windowSegments.length === 0) {
      return [];
    }

    const iconMap = new Map<string, {
      task: TaskWithState;
      centerMinute: number;
      shouldShow: boolean;
      duration: number;
    }>();

    windowSegments.forEach(segment => {
      if (!iconMap.has(segment.task.id)) {
        const task = segment.task;
        
        // Use VISIBLE task segments for icon position so it's always centered in the visible wedge
        const taskSegments = windowSegments.filter(s => s.task.id === task.id);
        
        if (taskSegments.length === 0) return;

        // Find the visual start and end of the combined segments
        // Segments from calculateTimeWindow are generally chronological (past -> future)
        const firstSegment = taskSegments[0];
        const lastSegment = taskSegments[taskSegments.length - 1];
        
        const startMin = firstSegment.visibleStartMin;
        const endMin = lastSegment.visibleEndMin;
        
        // Calculate center of visible wedge
        let centerMinute = startMin + ((endMin - startMin + (endMin < startMin ? 60 : 0)) / 2);
        if (centerMinute >= 60) centerMinute -= 60;
        
        // Calculate total visible duration
        const totalTaskDuration = taskSegments.reduce((sum, s) => {
          // Handle wrapping calculation for duration
          let dur = s.visibleEndMin - s.visibleStartMin;
          if (dur < 0) dur += 60;
          return sum + dur;
        }, 0);
        
        // Only hide if task has fully ended AND visible duration is tiny
        const taskEndedBeforeNow = task.endTime.getTime() <= currentTime.getTime();
        const shouldShow = !(taskEndedBeforeNow && totalTaskDuration <= 3);
        
        iconMap.set(task.id, {
          task: task,
          centerMinute: centerMinute,
          shouldShow,
          duration: totalTaskDuration
        });
      }
    });

    return Array.from(iconMap.values());
  }, [windowSegments, currentTime]);

  // Render numbers - always visible and always black for readability
  const renderNumbers = () => {
    if (theme.design.numberType === 'none') return null;

    const numbers = [];
    const isCardinal = theme.design.numberType === 'cardinal';

    // Render Hours
    for (let i = 0; i < 12; i++) {
      const hour = i === 0 ? 12 : i;
      
      // Skip if cardinal mode and not 12, 3, 6, 9
      if (isCardinal && hour % 3 !== 0) continue;

      const angle = i * 30 - 90; // -90 to start at 12 o'clock
      const angleRad = (angle * Math.PI) / 180;
      // Push hours slightly inward if showing minutes
      // Since we removed minutes, we can use the same radius for all themes or standardize it
      const radius = CLOCK_RADIUS - 35; // Standard position
      
      const x = CX + radius * Math.cos(angleRad);
      const y = CY + radius * Math.sin(angleRad);

      const fontSize = theme.id.startsWith('kids') ? "32" : "24";

      // Always use black color with a slight white outline/shadow for visibility
      numbers.push(
        <text
          key={`number-${i}`}
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          fontWeight={theme.id.startsWith('kids') ? "900" : "600"}
          fill="#000000"
          stroke="#FFFFFF"
          strokeWidth="0.5"
          paintOrder="stroke fill"
          style={{
            fontFamily: theme.design.fontFamily,
          }}
        >
          {hour}
        </text>
      );
    }

    // Minutes rendering removed as per feedback

    return numbers;
  };

  // Render ticks - always render minute ticks for visibility
  const renderTicks = () => {
    const ticks = [];
    // Use lines style for better visibility, with a dark color for contrast
    const tickColor = '#333333'; // Dark gray for visibility against any background

    for (let i = 0; i < 60; i++) {
      const isHour = i % 5 === 0;
      
      const angle = i * 6 - 90;
      const angleRad = (angle * Math.PI) / 180;
      
      // Position - ticks are at the edge of the clock
      const outerR = CLOCK_RADIUS - 8;
      const innerR = isHour ? CLOCK_RADIUS - 22 : CLOCK_RADIUS - 14;
      
      // Lines style for all ticks
      const start = {
        x: CX + outerR * Math.cos(angleRad),
        y: CY + outerR * Math.sin(angleRad)
      };
      const end = {
        x: CX + innerR * Math.cos(angleRad),
        y: CY + innerR * Math.sin(angleRad)
      };
      
      ticks.push(
        <line 
          key={`tick-${i}`} 
          x1={start.x} y1={start.y} 
          x2={end.x} y2={end.y} 
          stroke={tickColor} 
          strokeWidth={isHour ? 2.5 : 1}
          strokeLinecap="round"
        />
      );
    }
    return ticks;
  };

  const isRimStyle = theme.design.wedgeStyle === 'rim';
  const wedgeInnerRadius = isRimStyle ? WEDGE_RADIUS * 0.65 : 0;

  return (
    <div className={styles.clockContainer}>
      <svg 
        viewBox="0 0 500 500" 
        className={styles.clockSvg}
      >
        {/* Background */}
        {theme.design.background === 'gradient' ? (
           <defs>
             <linearGradient id="clock-bg-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
               <stop offset="0%" stopColor="#FFFFFF" />
               <stop offset="100%" stopColor={theme.colors.background} />
             </linearGradient>
           </defs>
        ) : null}

        <circle
          cx={CX}
          cy={CY}
          r={CLOCK_RADIUS}
          fill={theme.design.background === 'gradient' ? "url(#clock-bg-gradient)" : theme.colors.background}
          className={styles.dialBackground}
          style={{ filter: theme.id !== 'focus' ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.05))' : 'none' }}
        />
        
        {/* Border */}
        {theme.design.borderStyle !== 'none' && (
          <circle
            cx={CX}
            cy={CY}
            r={CLOCK_RADIUS}
            fill="none"
            stroke={theme.colors.border}
            strokeWidth={theme.design.borderStyle === 'thick' ? "12" : "4"}
          />
        )}

        {/* Ticks */}
        <g className={styles.ticksLayer}>
          {renderTicks()}
        </g>

        {/* Task wedges */}
        {windowSegments.length > 0 && (
          <g className={styles.wedgesLayer}>
            {windowSegments.map((segment, index) => {
              const nextSegment = windowSegments[index + 1];
              const nextIsSameTask = nextSegment && nextSegment.task.id === segment.task.id;
              
              const radius = taskRadii.get(segment.task.id)?.radius ?? segment.radius;
              
              // For rim style, if we layer, we adjust the inner radius or outer?
              // For simplicity, rim style ignores layering for now, or just shifts both
              // Let's keep it simple: standard behavior for now.
              const effectiveInner = wedgeInnerRadius;

              return (
                <TaskWedge
                  key={`segment-${segment.task.id}-${index}`}
                  task={segment.task}
                  cx={CX}
                  cy={CY}
                  radius={radius}
                  innerRadius={effectiveInner}
                  visibleStartMin={segment.visibleStartMin}
                  visibleEndMin={segment.visibleEndMin}
                  alphaStart={segment.alphaStart}
                  alphaEnd={segment.alphaEnd}
                  segmentIndex={index}
                  hasNextSegment={nextIsSameTask}
                  isHovered={segment.task.id === hoveredTaskId}
                  onHover={(t) => setHoveredTaskId(t.id)}
                  onLeave={() => setHoveredTaskId(null)}
                  onClick={onTaskClick}
                />
              );
            })}
          </g>
        )}

        {/* Subtle logo branding - positioned 45° down-right from center */}
        {/* Rendered after wedges but before icons/hands for proper layering */}
        <image
          href={DARK_THEMES.includes(themeId) ? CLOCK_LOGO_PATHS.white : CLOCK_LOGO_PATHS.black}
          x={LOGO_X - LOGO_SIZE / 2}
          y={LOGO_Y - LOGO_SIZE / 4}
          width={LOGO_SIZE}
          opacity={LOGO_OPACITY}
          style={{ pointerEvents: 'none' }}
        />

        {/* Numbers */}
        <g className={styles.numbers}>
          {renderNumbers()}
        </g>

        {/* Task icons */}
        {taskIcons.length > 0 && (
          <g className={styles.iconsLayer}>
            {taskIcons.map(iconInfo => {
              if (!iconInfo.shouldShow) return null;
              
              const radius = taskRadii.get(iconInfo.task.id)?.radius ?? WEDGE_RADIUS;
              const iconAngle = minutesToAngle(iconInfo.centerMinute);
              
              // Center position depends on wedge style
              // If rim: center between inner and outer
              // If full: 0.65 * radius
              const posRadius = isRimStyle 
                ? (wedgeInnerRadius + (radius - wedgeInnerRadius) / 2)
                : radius * 0.65;

              const iconPos = polarToCartesian(CX, CY, posRadius, iconAngle % 360);
              
              const taskSegments = windowSegments.filter(s => s.task.id === iconInfo.task.id);
              const avgAlpha = taskSegments.length > 0 
                ? taskSegments.reduce((sum, s) => sum + (s.alphaStart + s.alphaEnd) / 2, 0) / taskSegments.length 
                : 1.0;
              
              const isSmall = iconInfo.duration < 10 || radius < WEDGE_RADIUS - 30;
              // Smaller icons for rim style to fit
              const baseSize = isRimStyle ? 32 : 44;
              const fontSize = isSmall ? (baseSize * 0.7) : baseSize;
              
              const isHovered = iconInfo.task.id === hoveredTaskId;

              return (
                <g 
                  key={`icon-${iconInfo.task.id}`} 
                  style={{ 
                    opacity: avgAlpha,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    transformOrigin: `${iconPos.x}px ${iconPos.y}px`
                  }}
                  transform={isHovered ? 'scale(1.1)' : 'scale(1)'}
                  onMouseEnter={() => setHoveredTaskId(iconInfo.task.id)}
                  onMouseLeave={() => setHoveredTaskId(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick?.(iconInfo.task);
                  }}
                >
                  <text
                    x={iconPos.x}
                    y={iconPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fontSize}
                    style={{
                      pointerEvents: 'none', // Let the group handle events
                      filter: theme.id === 'kids' ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))' : 'none',
                      transition: 'font-size 0.3s ease',
                      fill: theme.id === 'focus' ? '#000' : undefined
                    }}
                  >
                    {iconInfo.task.icon}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {/* Clock hands - colored per the selected theme so theme changes are visible
            (and hands stay legible on dark vs light clock faces). */}
        <g className={styles.handsLayer}>
          {/* Hour Hand - shorter, thicker */}
          <line
            x1={CX}
            y1={CY}
            x2={CX + 100 * Math.cos((hour - 90) * Math.PI / 180)}
            y2={CY + 100 * Math.sin((hour - 90) * Math.PI / 180)}
            stroke={theme.colors.hands.hour}
            strokeWidth={theme.design.handStyle === 'fun' ? "16" : "8"}
            strokeLinecap={theme.design.handStyle === 'fun' ? "round" : "square"}
            className={styles.hourHand}
          />

          {/* Minute Hand - longer, medium thickness */}
          <line
            x1={CX}
            y1={CY}
            x2={CX + 150 * Math.cos((minute - 90) * Math.PI / 180)}
            y2={CY + 150 * Math.sin((minute - 90) * Math.PI / 180)}
            stroke={theme.colors.hands.minute}
            strokeWidth={theme.design.handStyle === 'fun' ? "12" : "6"}
            strokeLinecap={theme.design.handStyle === 'fun' ? "round" : "square"}
            className={styles.minuteHand}
          />

          {/* Second Hand - longest, thin */}
          <line
            x1={CX}
            y1={CY}
            x2={CX + 170 * Math.cos((second - 90) * Math.PI / 180)}
            y2={CY + 170 * Math.sin((second - 90) * Math.PI / 180)}
            stroke={theme.colors.hands.second}
            strokeWidth={theme.design.handStyle === 'fun' ? "5" : "2"}
            strokeLinecap={theme.design.handStyle === 'fun' ? "round" : "square"}
            className={styles.secondHand}
          />

          {/* Center Dot */}
          <circle
            cx={CX}
            cy={CY}
            r={theme.design.handStyle === 'fun' ? "14" : "8"}
            fill={theme.colors.hands.centerDot}
            stroke={theme.colors.hands.hour}
            strokeWidth={theme.design.handStyle === 'fun' ? "4" : "0"}
          />
        </g>
      </svg>
    </div>
  );
}
